import { toCompilationEntries, validatePolicyRevision, } from '../../compass-position-policy/dist/index.js';
export const ASSIGNMENT_SOURCE_KINDS = ['contact', 'tenantMembership'];
export const POSITION_RESPONSIBILITY_DIAGNOSTIC_CODES = [
    'EXCESSIVE_POSITION_GRAPH',
    'MALFORMED_POSITION',
    'MALFORMED_POSITION_ID',
    'DUPLICATE_POSITION_ID',
    'INVALID_COMMITTEE_ID',
    'MALFORMED_PARENT_POSITION_ID',
    'MISSING_PARENT_POSITION',
    'SELF_REFERENCE',
    'POSITION_HIERARCHY_CYCLE',
];
export const DEFAULT_MAX_POSITION_GRAPH_SIZE = 256;
const STABLE_POSITION_ID = /^[A-Za-z0-9][A-Za-z0-9_-]{2,127}$/;
/** Produce the existing semantic `pos_…` identity format once, at Position
 * creation or migration time. The persisted result, never the mutable name,
 * remains authoritative afterward. */
export function proposeStablePositionId(displayName) {
    const normalized = displayName
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    const proposed = normalized ? `pos_${normalized}` : '';
    return proposed && STABLE_POSITION_ID.test(proposed) ? proposed : null;
}
function frozenRecord(values) {
    return Object.freeze(Object.fromEntries([...values.entries()]
        .sort(([left], [right]) => compareStable(left, right))
        .map(([key, ids]) => [key, Object.freeze([...ids].sort(compareStable))])));
}
function positionDiagnosticKey(value) {
    return [value.code, String(value.index ?? ''), value.positionId ?? '', value.relatedPositionId ?? ''].join('\u0000');
}
/** Compile direct and inherited functional responsibility from one trusted
 * tenant Position configuration. Any structural defect disables the entire
 * supervisory layer while retaining independently valid direct committees. */
export function compilePositionResponsibilityGraph(definitions, knownCommitteeIds, options = {}) {
    const maxPositions = options.maxPositions ?? DEFAULT_MAX_POSITION_GRAPH_SIZE;
    const knownCommittees = new Set(knownCommitteeIds.filter(nonEmpty));
    const diagnostics = [];
    let structurallyValid = true;
    if (!Number.isSafeInteger(maxPositions) || maxPositions < 1 || definitions.length > maxPositions) {
        diagnostics.push({ code: 'EXCESSIVE_POSITION_GRAPH' });
        structurallyValid = false;
    }
    const rows = new Map();
    const direct = new Map();
    for (const [index, raw] of definitions.entries()) {
        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
            diagnostics.push({ code: 'MALFORMED_POSITION', index });
            structurallyValid = false;
            continue;
        }
        const value = raw;
        const positionId = typeof value.positionId === 'string' ? value.positionId.trim() : '';
        if (!positionId || !STABLE_POSITION_ID.test(positionId)) {
            diagnostics.push({ code: 'MALFORMED_POSITION_ID', index, ...(positionId ? { positionId } : {}) });
            structurallyValid = false;
            continue;
        }
        if (rows.has(positionId)) {
            diagnostics.push({ code: 'DUPLICATE_POSITION_ID', index, positionId });
            structurallyValid = false;
            continue;
        }
        let committeeId = null;
        if (value.committeeId != null && value.committeeId !== '') {
            if (typeof value.committeeId !== 'string' || !knownCommittees.has(value.committeeId)) {
                diagnostics.push({ code: 'INVALID_COMMITTEE_ID', index, positionId });
                structurallyValid = false;
            }
            else {
                committeeId = value.committeeId;
            }
        }
        let parentId = null;
        if (!Object.prototype.hasOwnProperty.call(value, 'reportsToPositionId')) {
            diagnostics.push({ code: 'MALFORMED_PARENT_POSITION_ID', index, positionId });
            structurallyValid = false;
        }
        else if (value.reportsToPositionId !== null) {
            if (typeof value.reportsToPositionId !== 'string'
                || !STABLE_POSITION_ID.test(value.reportsToPositionId.trim())) {
                diagnostics.push({ code: 'MALFORMED_PARENT_POSITION_ID', index, positionId });
                structurallyValid = false;
            }
            else {
                parentId = value.reportsToPositionId.trim();
            }
        }
        rows.set(positionId, { committeeId, parentId });
        direct.set(positionId, new Set(committeeId ? [committeeId] : []));
    }
    const children = new Map([...rows.keys()].map((id) => [id, new Set()]));
    for (const [positionId, row] of rows) {
        if (!row.parentId)
            continue;
        if (row.parentId === positionId) {
            diagnostics.push({ code: 'SELF_REFERENCE', positionId, relatedPositionId: row.parentId });
            structurallyValid = false;
        }
        else if (!rows.has(row.parentId)) {
            diagnostics.push({ code: 'MISSING_PARENT_POSITION', positionId, relatedPositionId: row.parentId });
            structurallyValid = false;
        }
        else {
            children.get(row.parentId).add(positionId);
        }
    }
    const cycleKeys = new Set();
    for (const start of [...rows.keys()].sort(compareStable)) {
        const path = [];
        const pathIndex = new Map();
        let current = start;
        let steps = 0;
        while (current && rows.has(current) && steps <= rows.size) {
            const repeatedAt = pathIndex.get(current);
            if (repeatedAt !== undefined) {
                const cycle = path.slice(repeatedAt).sort(compareStable);
                const key = cycle.join('\u0000');
                if (!cycleKeys.has(key)) {
                    cycleKeys.add(key);
                    diagnostics.push({ code: 'POSITION_HIERARCHY_CYCLE', positionId: cycle[0], relatedPositionId: cycle.at(-1) });
                }
                structurallyValid = false;
                break;
            }
            pathIndex.set(current, path.length);
            path.push(current);
            current = rows.get(current)?.parentId ?? null;
            steps += 1;
        }
        if (steps > rows.size) {
            diagnostics.push({ code: 'EXCESSIVE_POSITION_GRAPH', positionId: start });
            structurallyValid = false;
        }
    }
    const supervisedPositions = new Map([...rows.keys()].map((id) => [id, new Set()]));
    const supervisedCommittees = new Map([...rows.keys()].map((id) => [id, new Set()]));
    if (structurallyValid) {
        for (const root of [...rows.keys()].sort(compareStable)) {
            const visited = new Set();
            const queue = [...(children.get(root) ?? [])].sort(compareStable);
            while (queue.length) {
                const positionId = queue.shift();
                if (visited.has(positionId))
                    continue;
                visited.add(positionId);
                supervisedPositions.get(root).add(positionId);
                const committeeId = rows.get(positionId)?.committeeId;
                if (committeeId)
                    supervisedCommittees.get(root).add(committeeId);
                queue.push(...[...(children.get(positionId) ?? [])].sort(compareStable));
                if (visited.size > rows.size) {
                    diagnostics.push({ code: 'EXCESSIVE_POSITION_GRAPH', positionId: root });
                    structurallyValid = false;
                    break;
                }
            }
            if (!structurallyValid)
                break;
        }
    }
    if (!structurallyValid) {
        for (const ids of supervisedPositions.values())
            ids.clear();
        for (const ids of supervisedCommittees.values())
            ids.clear();
    }
    const effective = new Map();
    for (const positionId of rows.keys()) {
        effective.set(positionId, new Set([
            ...(direct.get(positionId) ?? []),
            ...(supervisedCommittees.get(positionId) ?? []),
        ]));
    }
    return Object.freeze({
        supervisionEnabled: structurallyValid,
        positionIds: Object.freeze([...rows.keys()].sort(compareStable)),
        directCommitteeIdsByPositionId: frozenRecord(direct),
        childPositionIdsByPositionId: frozenRecord(children),
        supervisedPositionIdsByPositionId: frozenRecord(supervisedPositions),
        supervisedCommitteeIdsByPositionId: frozenRecord(supervisedCommittees),
        effectiveCommitteeIdsByPositionId: frozenRecord(effective),
        diagnostics: Object.freeze(diagnostics.sort((a, b) => compareStable(positionDiagnosticKey(a), positionDiagnosticKey(b)))),
    });
}
export const AUTHORIZATION_DIAGNOSTIC_CODES = [
    'INVALID_POLICY_REVISION',
    'UNPUBLISHED_POLICY_REVISION',
    'POLICY_OWNER_MISMATCH',
    'UNRESOLVED_ASSIGNMENT',
    'AMBIGUOUS_ASSIGNMENT',
    'UNKNOWN_POSITION',
    'POSITION_WITHOUT_POLICY',
    'INVALID_ASSIGNMENT_SCOPE',
    'ASSIGNMENT_EXCEEDS_POSITION_LEVEL',
    'UNSUPPORTED_SCOPE_INTERSECTION',
    'SUBJECT_MISMATCH',
    'TENANT_MISMATCH',
    'V1_BEHAVIOR_NOT_REPRESENTED',
    'LEGACY_AUTHORITY_WITHOUT_V2_EQUIVALENT',
];
function compareStable(left, right) {
    return left < right ? -1 : left > right ? 1 : 0;
}
function nonEmpty(value) {
    return typeof value === 'string' && value.trim().length > 0;
}
function canonicalStrings(values) {
    return Object.freeze([...new Set(values.filter(nonEmpty))].sort(compareStable));
}
function scopeKey(scope) {
    if (scope.kind === 'self')
        return `self:${scope.userId}:${scope.selfKey ?? ''}`;
    if (scope.kind === 'subcommittee')
        return `subcommittee:${scope.districtId}:${canonicalStrings(scope.subcommitteeIds).join(',')}`;
    if (scope.kind === 'district')
        return `district:${scope.districtId}`;
    return `council:${scope.councilId}`;
}
function diagnosticKey(diagnostic) {
    return [
        diagnostic.code,
        diagnostic.assignmentId ?? '',
        diagnostic.positionId ?? '',
        diagnostic.capabilityKey ?? '',
        diagnostic.legacyEvidenceId ?? '',
        diagnostic.message,
        ...(diagnostic.details ?? []),
    ].join('\u0000');
}
function canonicalDiagnostics(diagnostics) {
    return Object.freeze([...diagnostics].sort((a, b) => compareStable(diagnosticKey(a), diagnosticKey(b))));
}
function isAssignmentScope(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return false;
    const scope = value;
    if (scope.kind === 'district')
        return nonEmpty(scope.districtId) && Object.keys(scope).every((key) => ['kind', 'districtId'].includes(key));
    if (scope.kind === 'subcommittee')
        return nonEmpty(scope.districtId)
            && Array.isArray(scope.subcommitteeIds)
            && scope.subcommitteeIds.length > 0
            && scope.subcommitteeIds.every(nonEmpty)
            && Object.keys(scope).every((key) => ['kind', 'districtId', 'subcommitteeIds'].includes(key));
    if (scope.kind === 'council')
        return nonEmpty(scope.councilId) && Object.keys(scope).every((key) => ['kind', 'councilId'].includes(key));
    if (scope.kind === 'self') {
        return nonEmpty(scope.userId)
            && (scope.selfKey === null || nonEmpty(scope.selfKey))
            && Object.keys(scope).every((key) => ['kind', 'userId', 'selfKey'].includes(key));
    }
    return false;
}
function validSubject(subject) {
    return nonEmpty(subject.tenantId)
        && nonEmpty(subject.userId)
        && (subject.selfKey === null || nonEmpty(subject.selfKey));
}
function sameSubject(left, right) {
    return left.tenantId === right.tenantId
        && left.userId === right.userId
        && left.selfKey === right.selfKey;
}
function provenanceFor(input) {
    return Object.freeze({
        sourceKind: input.source.kind,
        sourceRecordId: input.source.recordId,
        sourcePath: input.source.path,
        sourcePositionValue: input.sourcePositionValue,
    });
}
/**
 * Converts explicitly mapped V1 Contact/membership values to stable IDs.
 * It deliberately does not compare the V1 string to catalog displayName.
 */
export function adaptV1PositionAssignments(inputs, mappings, positionCatalog) {
    const catalog = new Map(positionCatalog.map((position) => [position.positionId, position]));
    const mapping = new Map();
    for (const item of mappings) {
        const candidates = mapping.get(item.sourcePositionValue) ?? new Set();
        for (const positionId of item.candidatePositionIds)
            candidates.add(positionId);
        mapping.set(item.sourcePositionValue, candidates);
    }
    const diagnostics = [];
    const assignments = [...inputs]
        .sort((a, b) => compareStable(a.assignmentId, b.assignmentId))
        .map((input) => {
        const provenance = provenanceFor(input);
        const candidates = canonicalStrings([...(mapping.get(input.sourcePositionValue) ?? [])]);
        const known = candidates.filter((positionId) => catalog.has(positionId));
        const unknown = candidates.filter((positionId) => !catalog.has(positionId));
        if (unknown.length > 0) {
            diagnostics.push({
                code: 'UNKNOWN_POSITION',
                assignmentId: input.assignmentId,
                message: 'The explicit V1 mapping references a Position ID absent from the catalog.',
                details: unknown,
            });
        }
        if (candidates.length === 0 || known.length === 0) {
            diagnostics.push({
                code: 'UNRESOLVED_ASSIGNMENT',
                assignmentId: input.assignmentId,
                message: 'The V1 Position value has no unique stable Position mapping.',
            });
            return Object.freeze({
                status: 'unresolved',
                assignmentId: input.assignmentId,
                subject: input.subject,
                actualScope: input.actualScope,
                provenance,
                candidatePositionIds: candidates,
            });
        }
        if (candidates.length !== 1 || known.length !== 1) {
            diagnostics.push({
                code: 'AMBIGUOUS_ASSIGNMENT',
                assignmentId: input.assignmentId,
                message: 'The V1 Position value maps to multiple stable Position candidates.',
                details: candidates,
            });
            return Object.freeze({
                status: 'ambiguous',
                assignmentId: input.assignmentId,
                subject: input.subject,
                actualScope: input.actualScope,
                provenance,
                candidatePositionIds: candidates,
            });
        }
        const position = catalog.get(known[0]);
        if (!isAssignmentScope(input.actualScope) || !validSubject(input.subject)) {
            diagnostics.push({
                code: 'INVALID_ASSIGNMENT_SCOPE',
                assignmentId: input.assignmentId,
                positionId: position.positionId,
                message: 'The V1 assignment does not contain a supported explicit scope or identity.',
            });
            return Object.freeze({
                status: 'unresolved',
                assignmentId: input.assignmentId,
                subject: input.subject,
                actualScope: input.actualScope,
                provenance,
                candidatePositionIds: candidates,
            });
        }
        return Object.freeze({
            status: 'resolved',
            assignmentId: input.assignmentId,
            subject: input.subject,
            positionId: position.positionId,
            positionOrganizationLevel: position.organizationLevel,
            actualScope: input.actualScope,
            provenance,
        });
    });
    return Object.freeze({ assignments: Object.freeze(assignments), diagnostics: canonicalDiagnostics(diagnostics) });
}
function validateScopeAgainstContext(assignment, subject, organization) {
    const scope = assignment.actualScope;
    if (!isAssignmentScope(scope)) {
        return { code: 'INVALID_ASSIGNMENT_SCOPE', assignmentId: assignment.assignmentId, positionId: assignment.positionId, message: 'Assignment scope is malformed.' };
    }
    if (scope.kind === 'self' && (scope.userId !== subject.userId || scope.selfKey !== subject.selfKey)) {
        return { code: 'INVALID_ASSIGNMENT_SCOPE', assignmentId: assignment.assignmentId, positionId: assignment.positionId, message: 'Self scope does not identify the compilation subject.' };
    }
    if (scope.kind === 'district' && !organization.districtIds.includes(scope.districtId)) {
        return { code: 'INVALID_ASSIGNMENT_SCOPE', assignmentId: assignment.assignmentId, positionId: assignment.positionId, message: 'District scope is outside the supplied organization context.' };
    }
    if (scope.kind === 'subcommittee' && !organization.districtIds.includes(scope.districtId)) {
        return { code: 'INVALID_ASSIGNMENT_SCOPE', assignmentId: assignment.assignmentId, positionId: assignment.positionId, message: 'Subcommittee scope is outside the supplied organization context.' };
    }
    if (scope.kind === 'council' && scope.councilId !== organization.councilId) {
        return { code: 'INVALID_ASSIGNMENT_SCOPE', assignmentId: assignment.assignmentId, positionId: assignment.positionId, message: 'Council scope is outside the supplied organization context.' };
    }
    if (assignment.positionOrganizationLevel === 'district' && scope.kind === 'council') {
        return { code: 'ASSIGNMENT_EXCEEDS_POSITION_LEVEL', assignmentId: assignment.assignmentId, positionId: assignment.positionId, message: 'A district Position assignment cannot have council scope.' };
    }
    return null;
}
function intersectScope(assignment, ceiling, subject) {
    const assignmentScope = assignment.actualScope;
    if (ceiling === 'self') {
        return Object.freeze({ kind: 'self', userId: subject.userId, selfKey: subject.selfKey });
    }
    if (assignmentScope.kind === 'self')
        return assignmentScope;
    if (assignmentScope.kind === 'subcommittee')
        return assignmentScope;
    if (ceiling === 'subcommittee') {
        if (assignmentScope.kind !== 'district')
            return null;
        // Council Access Phase A: only DIRECT subcommittee membership narrows
        // a subcommittee-ceiling grant's actual scope. supervisedSubcommitteeIds
        // (widened through the Position reporting graph — see
        // CanonicalPositionAssignment's doc comment) is deliberately excluded
        // here, so that holding a Position higher in the reporting hierarchy
        // never implicitly grants capabilities over everything it supervises —
        // a firm architectural rule ("organizational hierarchy must not
        // automatically cascade permissions"). The inherited set remains
        // available on the assignment for organizational display/routing
        // purposes; it is simply no longer an authorization input.
        const subcommitteeIds = canonicalStrings([...(assignment.directSubcommitteeIds ?? [])]);
        return subcommitteeIds.length
            ? Object.freeze({ kind: 'subcommittee', districtId: assignmentScope.districtId, subcommitteeIds })
            : null;
    }
    if (assignmentScope.kind === 'district')
        return assignmentScope;
    if (ceiling === 'council')
        return assignmentScope;
    // A council-wide assignment plus a district ceiling does not identify one
    // district. Expanding it to every district would manufacture authority.
    return null;
}
function contributorKey(contributor) {
    return [contributor.assignmentId, contributor.positionId, scopeKey(contributor.assignmentScope), contributor.policyScopeCeiling,
        contributor.provenance.sourceKind, contributor.provenance.sourceRecordId,
        contributor.provenance.sourcePath, contributor.provenance.sourcePositionValue].join('\u0000');
}
function comparisonKey(comparison) {
    return `${comparison.evidenceId}\u0000${comparison.mechanism}\u0000${comparison.disposition}`;
}
export function compareLegacyAuthorization(grants, expectations) {
    const grantKeys = new Set(grants.map((grant) => `${grant.capabilityKey}\u0000${scopeKey(grant.effectiveScope)}`));
    const diagnostics = [];
    const comparisons = expectations.map((expectation) => {
        let outcome;
        if (expectation.disposition === 'resourceRelationshipRule' || expectation.disposition === 'separateAdministrationPlane') {
            outcome = 'keptSeparate';
        }
        else if (expectation.disposition === 'retireLegacyDebt') {
            outcome = 'retire';
        }
        else if (expectation.capabilityKey && expectation.scope
            && grantKeys.has(`${expectation.capabilityKey}\u0000${scopeKey(expectation.scope)}`)) {
            outcome = 'represented';
        }
        else {
            outcome = 'missing';
            diagnostics.push(expectation.capabilityKey === null
                ? {
                    code: 'LEGACY_AUTHORITY_WITHOUT_V2_EQUIVALENT',
                    legacyEvidenceId: expectation.evidenceId,
                    message: expectation.description,
                }
                : {
                    code: 'V1_BEHAVIOR_NOT_REPRESENTED',
                    legacyEvidenceId: expectation.evidenceId,
                    capabilityKey: expectation.capabilityKey,
                    message: expectation.description,
                });
        }
        return Object.freeze({ ...expectation, outcome });
    }).sort((a, b) => compareStable(comparisonKey(a), comparisonKey(b)));
    return Object.freeze({
        comparisons: Object.freeze(comparisons),
        diagnostics: canonicalDiagnostics(diagnostics),
    });
}
/** Pure, deterministic, observe-only compilation. No output is authoritative. */
export function compileEffectiveAuthorization(input) {
    const diagnostics = [];
    const empty = (status) => Object.freeze({
        status,
        authoritative: false,
        subject: input.subject,
        organization: input.organization,
        policy: null,
        grants: Object.freeze([]),
        diagnostics: canonicalDiagnostics(diagnostics),
        legacyComparisons: Object.freeze([]),
    });
    if (!validSubject(input.subject)
        || input.organization.tenantId !== input.subject.tenantId
        || !nonEmpty(input.organization.councilId)) {
        diagnostics.push({ code: 'TENANT_MISMATCH', message: 'Subject and organization context do not identify one valid tenant.' });
        return empty('rejected');
    }
    const validationContext = { positions: input.positionCatalog };
    const validated = validatePolicyRevision(input.policyRevision, validationContext);
    if (!validated.valid || !validated.value) {
        diagnostics.push({
            code: 'INVALID_POLICY_REVISION',
            message: 'Position policy revision failed validation.',
            details: validated.errors.map((error) => `${error.code}:${error.path}`).sort(compareStable),
        });
        return empty('rejected');
    }
    const revision = validated.value;
    if (revision.state !== 'published') {
        diagnostics.push({ code: 'UNPUBLISHED_POLICY_REVISION', message: 'Only a published Position policy revision can be compiled.' });
        return empty('rejected');
    }
    if (revision.owner.kind !== 'council' || revision.owner.ownerId !== input.subject.tenantId) {
        diagnostics.push({ code: 'POLICY_OWNER_MISMATCH', message: 'Effective user compilation requires the subject tenant\'s council-owned policy.' });
        return empty('rejected');
    }
    const compilationRows = toCompilationEntries(revision, validationContext);
    if (!compilationRows.valid || !compilationRows.value || !revision.fingerprint) {
        diagnostics.push({ code: 'INVALID_POLICY_REVISION', message: 'Published Position policy did not produce valid compilation rows.' });
        return empty('rejected');
    }
    const catalog = new Map(input.positionCatalog.map((position) => [position.positionId, position]));
    const grantsByPosition = new Map();
    for (const row of compilationRows.value) {
        grantsByPosition.set(row.positionId, Object.freeze([...(grantsByPosition.get(row.positionId) ?? []), row]));
    }
    const aggregate = new Map();
    for (const assignment of [...input.assignments].sort((a, b) => compareStable(a.assignmentId, b.assignmentId))) {
        if (!sameSubject(assignment.subject, input.subject)) {
            diagnostics.push({ code: assignment.subject.tenantId === input.subject.tenantId ? 'SUBJECT_MISMATCH' : 'TENANT_MISMATCH', assignmentId: assignment.assignmentId, message: 'Assignment identity does not match the compilation subject.' });
            continue;
        }
        if (assignment.status === 'unresolved') {
            diagnostics.push({ code: 'UNRESOLVED_ASSIGNMENT', assignmentId: assignment.assignmentId, message: 'Unresolved assignment contributes no grants.' });
            continue;
        }
        if (assignment.status === 'ambiguous') {
            diagnostics.push({ code: 'AMBIGUOUS_ASSIGNMENT', assignmentId: assignment.assignmentId, message: 'Ambiguous assignment contributes no grants.', details: assignment.candidatePositionIds });
            continue;
        }
        const position = catalog.get(assignment.positionId);
        if (!position || position.organizationLevel !== assignment.positionOrganizationLevel) {
            diagnostics.push({ code: 'UNKNOWN_POSITION', assignmentId: assignment.assignmentId, positionId: assignment.positionId, message: 'Assignment Position is absent from, or inconsistent with, the stable catalog.' });
            continue;
        }
        const invalidScope = validateScopeAgainstContext(assignment, input.subject, input.organization);
        if (invalidScope) {
            diagnostics.push(invalidScope);
            continue;
        }
        const positionRows = grantsByPosition.get(assignment.positionId) ?? [];
        if (positionRows.length === 0) {
            diagnostics.push({ code: 'POSITION_WITHOUT_POLICY', assignmentId: assignment.assignmentId, positionId: assignment.positionId, message: 'The stable Position has no entry or grants in this policy revision.' });
            continue;
        }
        for (const row of positionRows) {
            const effectiveScope = intersectScope(assignment, row.scopeCeiling, input.subject);
            if (!effectiveScope) {
                diagnostics.push({
                    code: 'UNSUPPORTED_SCOPE_INTERSECTION',
                    assignmentId: assignment.assignmentId,
                    positionId: assignment.positionId,
                    capabilityKey: row.capabilityKey,
                    message: 'Council assignment plus district ceiling does not identify a district and cannot be broadened to all districts.',
                });
                continue;
            }
            const key = `${row.capabilityKey}\u0000${scopeKey(effectiveScope)}`;
            const item = aggregate.get(key) ?? { capabilityKey: row.capabilityKey, scope: effectiveScope, contributors: [] };
            item.contributors.push(Object.freeze({
                assignmentId: assignment.assignmentId,
                positionId: assignment.positionId,
                positionOrganizationLevel: assignment.positionOrganizationLevel,
                assignmentScope: assignment.actualScope,
                policyScopeCeiling: row.scopeCeiling,
                provenance: assignment.provenance,
            }));
            aggregate.set(key, item);
        }
    }
    const grants = [...aggregate.entries()].sort(([a], [b]) => compareStable(a, b)).map(([, item]) => Object.freeze({
        capabilityKey: item.capabilityKey,
        effectiveScope: item.scope,
        contributors: Object.freeze(item.contributors.sort((a, b) => compareStable(contributorKey(a), contributorKey(b)))),
        policyRevisionId: revision.revisionId,
        policyRevisionNumber: revision.revisionNumber,
        policyOwner: revision.owner,
        policyFingerprint: revision.fingerprint,
    }));
    const comparison = compareLegacyAuthorization(grants, input.legacyExpectations ?? []);
    diagnostics.push(...comparison.diagnostics);
    return Object.freeze({
        status: 'compiled',
        authoritative: false,
        subject: input.subject,
        organization: Object.freeze({
            ...input.organization,
            districtIds: canonicalStrings(input.organization.districtIds),
        }),
        policy: Object.freeze({
            revisionId: revision.revisionId,
            revisionNumber: revision.revisionNumber,
            owner: revision.owner,
            fingerprint: revision.fingerprint,
        }),
        grants: Object.freeze(grants),
        diagnostics: canonicalDiagnostics(diagnostics),
        legacyComparisons: comparison.comparisons,
    });
}
